<template>
  <div class="position-relative">
    <transition name="full-export-fade">
      <div v-if="isFetchingFullExport" class="full-export-overlay">
        <i class="el-icon-loading full-export-overlay__spinner"></i>
        <p class="full-export-overlay__title">Preparing your full data export</p>
        <p class="full-export-overlay__sub">This may take a moment…</p>
      </div>
    </transition>
    <CombinedExportWarningModal
      v-model="showCombinedExportModal"
      @confirm="handleConfirmCombinedExport"
      @cancel="handleCancelCombinedExport"
    />
    <SaveReportWizardFullExportConfirmModal
      v-model="showFullExportConfirmModal"
      @continue="onFullExportModalContinue"
      @select-filters="onSelectFiltersFromModal"
    />

    <transition name="full-export-fade">
      <div v-if="exportConfirmed && exportSummary" class="pt-4">
        <b-alert show dismissible variant="success" class="mb-4" @dismissed="dismissExportSuccess">
          Export submitted. Your Full Data Export will be delivered shortly. All recipients will receive a secure download link.
        </b-alert>
        <div class="export-summary-card card p-4">
          <h5 class="font-weight-bold mb-3">Export Summary</h5>
          <dl class="export-summary-card__list mb-0">
            <div class="export-summary-card__row">
              <dt>Export Name</dt>
              <dd>{{ exportSummary.name || '—' }}</dd>
            </div>
            <div class="export-summary-card__row">
              <dt>Export Type</dt>
              <dd>{{ exportSummary.type }}</dd>
            </div>
            <div class="export-summary-card__row">
              <dt>Lead Underwriter</dt>
              <dd>{{ exportSummary.underwriter }}</dd>
            </div>
            <div class="export-summary-card__row">
              <dt>Requested By</dt>
              <dd>{{ exportSummary.requestedBy }}</dd>
            </div>
            <div class="export-summary-card__row">
              <dt>Requested Date</dt>
              <dd>{{ exportSummary.requestedDate }}</dd>
            </div>
            <div class="export-summary-card__row">
              <dt>Recipients</dt>
              <dd>{{ exportSummary.recipients.length ? exportSummary.recipients.join(', ') : '—' }}</dd>
            </div>
            <div v-if="exportSummary.scheduleStatus" class="export-summary-card__row">
              <dt>Schedule Status</dt>
              <dd>{{ exportSummary.scheduleStatus }}</dd>
            </div>
          </dl>
        </div>
      </div>
    </transition>

    <form-wizard v-if="!exportConfirmed"
        ref="stepWizard"
        @on-change="onWizardChange"
        id="save-report-wizard"
        :class="['p-0 mt-4 position-relative', { 'full-export-mode': isFullDataExport }]"
        color="#1c4a50"
        subtitle=""
        shape="circle"
        title=""
    >
      <b-progress :value="progressValue" :max="100" class="mb-3"></b-progress>

      <tab-content title="Select Reports">
        <SaveReportWizardUnderwriterSelect
            v-if="isSelectingUnderwriter"
            :default-role-value="currentUser.company && currentUser.company.role_value"
            :initial-id="pendingUnderwriter ? pendingUnderwriter.id : null"
            @select="onUnderwriterSelected"
            @change="(uw) => pendingUnderwriter = uw"
        />
        <SaveReportWizardReports
            v-else-if="1 <= activeStep"
            v-loading="isFetchingReport"
            @select="onSelectReport"
            @full-export="showFullExportConfirmModal = true"
        />
      </tab-content>

      <tab-content
          title="Select Filters"
          :before-change="handleBeforeCriteriasChange"
      >
        <SaveReportWizardCriterias
            v-if="2 <= activeStep"
            ref="criteriasRef"
            @reset-combined-export-warning="handleCancelCombinedExport"
            v-model="savedReport.payload.parameters"
            @set-flags="(payload) => (savedReport.payload.flag = payload)"
            @set-jurisdiction="(payload) => (savedReport.payload.jurisdiction = payload)"
            @set-umr-criteria="
            (payload) => (savedReport.payload.umr_criteria = payload)
          "
            @set-bordeaux-status="
            (payload) => (savedReport.payload.umr_criteria = payload)
          "
            :flag-status-set="savedReport.payload.flag"
            :jurisdiction="savedReport.payload.jurisdiction"
            :bordeaux-status="savedReport.payload.umr_criteria"
            :date-type="criteriaDateType"
            @set-date-type="
            (newCriteriaDateType) => (criteriaDateType = newCriteriaDateType)
          "
            :underwriter="savedReport.payload.uw_option"
            @set-underwriter="
            (newUnderwriter) => (savedReport.payload.uw_option = newUnderwriter)
          "
        />
      </tab-content>
      <!-- LIQ-2190 — Cash Call Reconciliation (TPA New Report only; a Full Data
           Export bypasses this step entirely, with no on-screen reference to
           the bypass). Sits between Select Filters and Split Categories so the
           netted figures carry forward into category splitting and the final
           exported BDX sheets. -->
      <tab-content
          title="Cash Call Reconciliation"
          :before-change="handleBeforeCashCallReconciliationChange"
      >
        <!-- confirmed-rows feeds the user's own decisions back in when this
             component is re-created. v-if destroys it every time the step is
             left, and a fresh mount would otherwise re-scan and re-emit the
             server's suggested state - silently discarding every
             Paid/Outstanding toggle and Cash Call Action set before the user
             reaches Export/Save. -->
        <SaveReportWizardCashCallReconciliation
            v-if="3 <= activeStep"
            ref="cashCallReconciliationRef"
            :export-payload="exportPayload"
            :is-full-data-export="isFullDataExport"
            :confirmed-rows="(cashCallReconciliation && cashCallReconciliation.rows) || []"
            @input="(payload) => (cashCallReconciliation = payload)"
        />
      </tab-content>
      <tab-content title="Split Categories">
        <template v-if="4 <= activeStep">
          <SaveReportWizardMarketExport
              v-model="savedReport.payload.export_type"
              class="my-4"
          />

          <hr />

          <SaveReportWizardSplitBy
              v-model="savedReport.payload.export_by"
              :exportType="savedReport.payload.export_type"
              @input="onSetSplitBy"
              @set-export-type="savedReport.payload.export_type = $event"
          />
        </template>
      </tab-content>
      <tab-content title="Select Columns">
        <SaveReportWizardColumns
            ref="columnsRef"
            v-if="5 <= activeStep"
            v-model="savedReport.payload.columns_to_show"
            :export-type="savedReport.payload.export_type"
            :export-sub-type="savedReport.payload.export_sub_type"
            :is-template-loaded="isTemplateLoaded"
            :is-superuser="currentUser.is_superuser"
            :is-full-data-export="isFullDataExport"
            :is-underwriter="isUnderwriterExport"
            :jurisdiction-list="savedReport.payload.jurisdiction"
            @export-sub-type="updateExportSubType"
        />
      </tab-content>
      <tab-content title="Export/Save" :before-change="() => !isFullDataExport">
        <template v-if="6 <= activeStep">
          <SaveReportWizardGroupByBroker
              v-if="!isFullDataExport"
              v-model="savedReport.payload.group_by_selection"
              class="mb-4"
          />

          <SaveReportWizardExport
              @set-export-schedule="(payload) => (reportSchedule = payload)"
              @set-schedule-active="(active) => (scheduleActive = active)"
              @set-recipients="(payload) => (savedReport.payload.recipients = payload)"
              @set-tpa-group-name="onSetTpaGroupName"
              :company-id="isFullDataExport ? (selectedUnderwriter ? selectedUnderwriter.id : userCompanyId) : null"
              :restrict-to-company="isFullDataExport"
              :is-underwriter="isUnderwriterExport"
              :initial-recipients="savedReport.payload.recipients || []"
              :initial-schedule="initialSchedule"
              :initial-tpa-group="Array.isArray(savedReport.payload.columns_to_show) && savedReport.payload.columns_to_show.includes('tpa_group_name')"
          />
        </template>
      </tab-content>

      <template slot="footer" slot-scope="props">
        <div
            :id="`wizard-footer-${props.activeTabIndex}`"
            class="d-flex align-items-center justify-content-between w-100"
        >
          <!-- Far left: Previous -->
          <div>
            <base-button
                v-if="(props.activeTabIndex > 0 && !isFullDataExport) || canShowPreviousInFullExport"
                @click.native="isFullDataExport ? onFullExportPrevious() : props.prevTab()"
                :disabled="isProcessing"
                :style="props.fillButtonStyle"
                type="primary"
            >Previous</base-button
            >
          </div>

          <!-- Far right: all other controls -->
          <div class="d-flex align-items-center">
            <base-button
                v-if="isSelectingUnderwriter && props.activeTabIndex === 0"
                @click.native="onUnderwriterSelected(pendingUnderwriter)"
                :disabled="!pendingUnderwriter"
                :style="props.fillButtonStyle"
                type="primary"
            >Next</base-button
            >

            <template v-if="props.activeTabIndex">
              <base-button
                  v-if="2 == activeStep"
                  @click="handleClearCriteriaFilters"
                  type="primary"
                  outline
                  class="mr-2"
              >Clear</base-button
              >

              <base-button
                  v-if="!props.isLastStep"
                  @click.native="handleNext(props)"
                  :disabled="!canProceedToNext"
                  :style="props.fillButtonStyle"
                  type="primary"
              >Next</base-button
              >

              <div v-else>
                <b-input-group>
                  <validation-provider rules="required" slim>
                    <b-form-input
                        v-model="activeReport.name"
                        placeholder="Title"
                        class="py-1"
                        style="max-height: 36px"
                    ></b-form-input>
                  </validation-provider>
                  <b-input-group-append>
                    <base-button
                        @click="saveReportTemplate"
                        :disabled="!activeReport.name || isSaving"
                        :loading="isSaving"
                        type="primary"
                        class="mr-2 rounded-right"
                    >Save & Export</base-button
                    >
                    <base-button
                        @click="exportReport"
                        :disabled="isExporting || scheduleActive"
                        :loading="isExporting"
                        type="primary"
                        class="rounded"
                        v-b-tooltip.hover="scheduleActive ? 'Disabled while a scheduled export is being configured. Turn off Schedule to run a one-time export.' : ''"
                    >One Time Export</base-button
                    >
                  </b-input-group-append>
                </b-input-group>
              </div>
            </template>
          </div>
        </div>
      </template>
    </form-wizard>
  </div>
</template>

<script>
import 'vue-form-wizard/dist/vue-form-wizard.min.css'

import { FormWizard, TabContent } from 'vue-form-wizard'
import { find } from 'lodash-es'

import { userMixin } from '@mixins'
import { ExportsService, ReportsService } from '@services'
import { claimingGroupOptions, splitByOptions } from './utils'
import exportColumnsNew from './utils/exportColumnsNew'

import CombinedExportWarningModal from './components/SaveReportWizardCombinedExportModal.vue'
import SaveReportWizardFullExportConfirmModal from './components/SaveReportWizardFullExportConfirmModal.vue'
import SaveReportWizardUnderwriterSelect from './components/SaveReportWizardUnderwriterSelect.vue'
import SaveReportWizardReports from './SaveReportWizardReports.vue'
import SaveReportWizardColumns from './SaveReportWizardColumns.vue'
import SaveReportWizardCriterias from './SaveReportWizardCriterias.vue'
import SaveReportWizardMarketExport from './components/SaveReportWizardMarketExport.vue'
import SaveReportWizardSplitBy from './components/SaveReportWizardSplitBy.vue'
import SaveReportWizardGroupByBroker from './components/SaveReportWizardGroupByBroker.vue'
import SaveReportWizardPreview from './SaveReportWizardPreview.vue'
import SaveReportWizardExport from './SaveReportWizardExport.vue'
import SaveReportWizardCashCallReconciliation from './components/SaveReportWizardCashCallReconciliation.vue'

const DEFAULT_EXPORT_BY = 'umr-risk_code-lloyds_cat_code-section_no-settlement_currency-year_of_account'
const FULL_EXPORT_COLUMNS = exportColumnsNew.filter(col => col.default && !col.region).map(col => col.value)

export default {
  components: {
    FormWizard,
    TabContent,
    SaveReportWizardReports,
    SaveReportWizardColumns,
    SaveReportWizardCriterias,
    SaveReportWizardMarketExport,
    SaveReportWizardSplitBy,
    SaveReportWizardGroupByBroker,
    SaveReportWizardPreview,
    SaveReportWizardExport,
    SaveReportWizardCashCallReconciliation,
    CombinedExportWarningModal,
    SaveReportWizardFullExportConfirmModal,
    SaveReportWizardUnderwriterSelect
  },
  data() {
    return {
      isExporting: false,
      isFetchingReport: false,
      isSaving: false,
      activeStep: 1,
      activeReport: {},
      reportSchedule: {},
      // LIQ-2190 — the confirmed Cash Call Reconciliation for this export: one
      // entry per matched claim row carrying its chosen Cash Call Action, the
      // Paid/Outstanding status to write back, and the derived Export Paid This
      // Month / Included in Totals figures the export applies.
      cashCallReconciliation: { rows: [] },
      // Saved schedule (start/end date, frequency, day, time, timezone) restored
      // when amending an existing template, passed to the Export step's scheduler
      // so previously-entered values (e.g. End Date) repopulate instead of resetting.
      initialSchedule: null,
      // Holds the export payload
      savedReport: {
        payload: {}
      },
      criteriaDateType: null,
      isTemplateLoaded: false,
      showCombinedExportModal: false,
      showFullExportConfirmModal: false,
      pendingNextProps: null,
      isFullDataExport: false,
      // True while the "Schedule" toggle is on in the Export step. Used to
      // disable the "One Time Export" option (a scheduled export and a one-time
      // export are mutually exclusive).
      scheduleActive: false,
      isFetchingFullExport: false,
      isSelectingUnderwriter: false,
      isGoingBackToUnderwriter: false,
      selectedUnderwriter: null,
      pendingUnderwriter: null,
      exportConfirmed: false,
      exportSummary: null,
      // Whether the TPA Group Name column should be included in this FDE.
      // Tracks the non-superuser Advanced Fields toggle selection; superusers
      // get it by default via the Select Columns checkbox instead.
      tpaGroupSelected: false
    }
  },
  mixins: [userMixin],
  computed: {
    isProcessing() {
      return this.isExporting || this.isSaving
    },
    progressValue() {
      // Six steps since LIQ-2190 added Cash Call Reconciliation.
      return (this.activeStep / 6) * 100
    },
    hasSelectedPartyFilter() {
      const payload = this.savedReport.payload

      return (
          !!find(payload.parameters, ['name', 'Underwriter_Name']) ||
          !!find(payload.parameters, ['name', 'Coverholder_Name']) ||
          !!find(payload.parameters, ['name', 'TPA_Name'])
      )
    },
    hasSelectedProgramFilter() {
      const payload = this.savedReport.payload

      return !!find(payload.parameters, ['name', 'Unique_Market_Reference_UMR'])
    },
    hasSelectedReportDateFilter() {
      const payload = this.savedReport.payload

      if (!this.criteriaDateType) return false

      const selectedReportingDate = find(payload.parameters, [
        'name',
        'Reporting_Period_End_Date'
      ])

      return this.criteriaDateType && !!selectedReportingDate
    },
    hasSelectedSubmissionDateFilter() {
      const payload = this.savedReport.payload

      if (!this.criteriaDateType) return false

      const selectedSubmissionDate = find(payload.parameters, [
        'name',
        'modified_on'
      ])

      return this.criteriaDateType && !!selectedSubmissionDate
    },
    hasExportByFilter() {
      const payload = this.savedReport.payload

      return undefined !== payload.export_by
    },
    canProceedToNext() {
      if (this.isProcessing) return false

      const payload = this.savedReport.payload

      if (
          'admin' != this.userCompanyRole &&
          'admin' != this.userCompanyRoleValue
      ) {
        if (2 == this.activeStep) {
          if (!this.hasSelectedPartyFilter && !this.hasSelectedProgramFilter)
            return false

          if (
              this.criteriaDateType !== 'all-exports' &&
              !this.hasSelectedReportDateFilter &&
              !this.hasSelectedSubmissionDateFilter
          )
            return false
          if (!payload.parameters.length && this.criteriaDateType !== 'all-exports') return false
        } else if (4 == this.activeStep) {
          if (!this.hasExportByFilter) return false
        }

        return true
      } else {
        if (5 == this.activeStep) {
          return payload.columns_to_show.length
        } else if (2 == this.activeStep) {
          if (!this.hasSelectedPartyFilter && !this.hasSelectedProgramFilter)
            return false

          if (
              this.criteriaDateType !== 'all-exports' &&
              !this.hasSelectedReportDateFilter &&
              !this.hasSelectedSubmissionDateFilter
          )
            return false

          if (!payload.parameters.length && this.criteriaDateType !== 'all-exports') return false
        } else if (4 == this.activeStep) {
          if (!this.hasExportByFilter) return false
        }

        return true
      }
    },
    canShowPreviousInFullExport() {
      if (!this.isFullDataExport) return false
      if (this.currentUser.is_superuser) {
        // Superuser: Previous on Underwriter Select, Select Columns, Export/Save
        return (this.activeStep === 1 && this.isSelectingUnderwriter) ||
               this.activeStep === 5 ||
               this.activeStep === 6
      }
      // Non-superuser: now goes through Select Columns (5) then Export/Save (6),
      // so Previous shows on both.
      return this.activeStep === 5 || this.activeStep === 6
    },
    exportPayload() {
      const payload = { ...this.savedReport.payload }

      if (!payload.export_by) payload.export_by = undefined

      if (payload.group_by.includes('*'))
        payload.group_by = claimingGroupOptions
            .filter((item) => '*' !== item.value)
            .map((item) => item.value)

      payload.is_full_data_export = this.isFullDataExport

      // The zip filename's COMPANY segment is the company RUNNING the export —
      // i.e. the logged-in user's own company — NOT the selected underwriter.
      // For a superuser, company_id is set to the selected underwriter, so we
      // send the requester's own company name separately for the filename.
      payload.requester_company_name =
        (this.currentUser && this.currentUser.company && this.currentUser.company.name) || null

      // Capture the requester's timezone so the backend can stamp the Full Data
      // Export filename in the runner's LOCAL time (e.g. a London user sees
      // London time, not UTC). Scheduled runs use the schedule's own timezone;
      // this covers one-time / Save & Export runs.
      try {
        payload.client_timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      } catch (e) {
        payload.client_timezone = null
      }

      return payload
    },
    isUnderwriterExport() {
      // TPA Group Name is underwriter-only for now. Superusers always qualify (they
      // run exports as an underwriter); others only if their role is Underwriter.
      return this.currentUser.is_superuser || this.userCompanyRole === 'Underwriter'
    }
  },
  methods: {
    buildExportSummary(type) {
      const now = new Date()
      const dateStr = now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      const timeStr = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
      const schedule = this.reportSchedule
      let scheduleStatus = null
      if (type === 'Scheduled Export' && schedule && schedule.occurrence) {
        scheduleStatus = schedule.occurrence
        if (schedule.timezone) scheduleStatus += ` · ${schedule.timezone}`
      }
      return {
        name: this.activeReport.name || null,
        type,
        underwriter: this.selectedUnderwriter ? this.selectedUnderwriter.name : (this.currentUser.company && this.currentUser.company.name) || '—',
        requestedBy: this.currentUser.email,
        requestedDate: `${dateStr} ${timeStr}`,
        recipients: this.savedReport.payload.recipients || [],
        scheduleStatus
      }
    },
    dismissExportSuccess() {
      this.exportConfirmed = false
      this.exportSummary = null
      this.$emit('report-exported', true)
    },
    handleNext(props) {
      if (this.criteriaDateType === 'all-exports' && this.activeStep === 2){
        this.pendingNextProps = props
        this.showCombinedExportModal = true
        return
      }
      props.nextTab()
    },
    handleConfirmCombinedExport() {
      this.showCombinedExportModal = false
      if (this.pendingNextProps && typeof this.pendingNextProps.nextTab === 'function') this.pendingNextProps.nextTab();
      this.pendingNextProps = null
    },
    handleCancelCombinedExport() {
      this.showCombinedExportModal = false
      this.pendingNextProps = null
    },
    updateExportSubType(newSubType) {
      this.savedReport.payload.export_sub_type = newSubType;
    },
    onWizardChange(prevIndex, nextIndex) {
      this.activeStep = nextIndex + 1
      if (nextIndex === 0 && this.isFullDataExport) {
        if (this.isGoingBackToUnderwriter) {
          // Superuser going back from Select Columns to Underwriter Select
          this.pendingUnderwriter = this.selectedUnderwriter
          this.isSelectingUnderwriter = true
          this.selectedUnderwriter = null
          this.isGoingBackToUnderwriter = false
        } else {
          // Cancel / hard reset — navigated away from export flow
          this.isFullDataExport = false
          this.isSelectingUnderwriter = false
          this.selectedUnderwriter = null
          this.pendingUnderwriter = null
          this.savedReport.payload = {}
          this.$nextTick(() => {
            if (this.$refs.stepWizard) this.$refs.stepWizard.reset()
          })
        }
      }
    },
    async onSelectReport(reportId) {
      this.isFullDataExport = false
      this.activeReport = {
        id: null,
        name: null,
        created_by: this.currentUser.id
      }
      this.isTemplateLoaded = false

      // Holds the original export payload
      this.savedReport.origPayload = {
        columns_to_show: [],
        export_by: DEFAULT_EXPORT_BY,
        group_by: claimingGroupOptions
            .map((item) => item.value)
            .filter((value) => '*' !== value),
        flag: [],
        parameters: [],
        company_id: this.userCompanyId,
        role: this.userCompanyRole,
        role_value: this.userCompanyRoleValue,
        umr_criteria: 'Active',
        group_by_selection: '',
        uw_option: 'Lead',
        export_type: 'market_export',
        jurisdiction: [],
        export_sub_type: 'lloyds',
        all_exports: false,
        recipients: []
      } // Non-reactive
      this.savedReport.payload = {
        columns_to_show: [],
        export_by: DEFAULT_EXPORT_BY,
        group_by: claimingGroupOptions
            .map((item) => item.value)
            .filter((value) => '*' !== value),
        flag: [],
        parameters: [],
        company_id: this.userCompanyId,
        role: this.userCompanyRole,
        role_value: this.userCompanyRoleValue,
        umr_criteria: 'Active',
        group_by_selection: '',
        uw_option: 'Lead',
        export_type: 'market_export',
        jurisdiction: [],
        export_sub_type: 'lloyds',
        all_exports: false,
        recipients: []
      } // Reactive

      if (reportId) await this.getSavedReportTemplate(reportId)

      // A full-data-export template (set in getSavedReportTemplate) skips the
      // Select Filters / Cash Call Reconciliation / Split steps and lands on
      // Select Columns (tab index 4 since LIQ-2190 inserted the reconciliation
      // step) for ALL users — same as the normal full export flow
      // (onFullDataExport). This is also how an FDE bypasses the cash call
      // reconciliation step, with no on-screen reference to the bypass.
      if (this.isFullDataExport) {
        const targetTab = 4
        await this.$nextTick()
        if (document.activeElement) document.activeElement.blur()
        this.$refs.stepWizard.changeTab(0, targetTab)
        return
      }

      this.$refs.stepWizard.nextTab()
    },
    onSelectFiltersFromModal() {
      this.onSelectReport(null)
    },
    onFullExportModalContinue() {
      if (this.currentUser.is_superuser) {
        this.isFullDataExport = true
        this.isSelectingUnderwriter = true
      } else {
        this.onFullDataExport()
      }
    },
    onUnderwriterSelected(underwriter) {
      this.selectedUnderwriter = underwriter
      this.isSelectingUnderwriter = false
      this.onFullDataExport()
    },
    onFullExportPrevious() {
      // Tab indices are 0-based and shifted by one since LIQ-2190 inserted the
      // Cash Call Reconciliation step at tab 2: Split Categories is now tab 3,
      // Select Columns tab 4 and Export/Save tab 5.
      if (this.activeStep === 6) {
        // Both user types: Export/Save → Select Columns
        this.$refs.stepWizard.changeTab(5, 4)
      } else if (this.activeStep === 5) {
        if (this.currentUser.is_superuser) {
          // Superuser: Select Columns → Underwriter Select (isSelectingUnderwriter restored in onWizardChange)
          this.isGoingBackToUnderwriter = true
          this.$refs.stepWizard.changeTab(4, 0)
        } else {
          // Non-superuser: no underwriter-select step — Previous from Select
          // Columns exits the full export flow back to Select Reports.
          this.isFullDataExport = false
          this.savedReport.payload = {}
          this.$nextTick(() => {
            if (this.$refs.stepWizard) this.$refs.stepWizard.reset()
          })
        }
      } else if (this.activeStep === 1 && this.isSelectingUnderwriter) {
        // Underwriter Select → Select Reports
        // Reset all export state AND wizard visited-tab cache so no forward pill stays clickable
        this.isSelectingUnderwriter = false
        this.isFullDataExport = false
        this.selectedUnderwriter = null
        this.pendingUnderwriter = null
        this.savedReport.payload = {}
        this.$nextTick(() => {
          if (this.$refs.stepWizard) this.$refs.stepWizard.reset()
        })
      }
    },
    async onFullDataExport() {
      this.isFetchingFullExport = true
      this.isFullDataExport = true
      // Fresh FDE: non-superusers opt in to TPA Group Name via the Advanced Fields
      // toggle; superusers get it checked by default in Select Columns.
      this.tpaGroupSelected = false
      this.activeReport = { id: null, name: null, created_by: this.currentUser.id }

      const isSuper = this.currentUser.is_superuser
      const role = isSuper ? 'Underwriter' : this.userCompanyRole
      const roleValue = isSuper && this.selectedUnderwriter
        ? this.selectedUnderwriter.role_value
        : this.userCompanyRoleValue
      const companyId = isSuper && this.selectedUnderwriter
        ? this.selectedUnderwriter.id
        : this.userCompanyId

      // Preserve columns if the same company is re-selected after going back
      const existingPayload = this.savedReport.payload
      const sameCompany = existingPayload && existingPayload.company_id === companyId
      const columnsToShow = sameCompany && Array.isArray(existingPayload.columns_to_show) && existingPayload.columns_to_show.length
        ? existingPayload.columns_to_show
        : FULL_EXPORT_COLUMNS

      const roleParamMap = {
        'Underwriter': 'Underwriter_Name',
        'TPA': 'TPA_Name',
        'Coverholder': 'Coverholder_Name'
      }
      const partyParam = roleParamMap[role]
      const parameters = partyParam && roleValue
        ? [{ name: partyParam, value: [roleValue] }]
        : []

      let jurisdiction = []
      try {
        const response = await ReportsService.getExportParametersV2({
          company_id: companyId,
          role,
          role_value: roleValue,
          umr_criteria: 'All',
          uw_option: 'Lead',
          all_exports: true,
          parameters,
          flag: []
        })
        jurisdiction = [...new Set((response.data.data.data.Jurisdiction || []).filter(Boolean))]
      } catch (e) {
        console.warn('[FDE] getExportParametersV2 failed — proceeding with empty jurisdiction:', e)
      }

      this.savedReport.payload = {
        columns_to_show: columnsToShow,
        export_by: DEFAULT_EXPORT_BY,
        group_by: claimingGroupOptions.map((item) => item.value).filter((value) => '*' !== value),
        flag: [],
        parameters,
        company_id: companyId,
        role,
        role_value: roleValue,
        umr_criteria: 'All',
        group_by_selection: '',
        uw_option: 'Lead',
        export_type: 'custom_export',
        jurisdiction,
        export_sub_type: 'lloyds',
        all_exports: true,
        recipients: []
      }

      // All users land on Select Columns (tab index 4 since LIQ-2190 inserted
      // the Cash Call Reconciliation step) first, mirroring the normal New
      // Report flow (Columns → Export/Save). A Full Data Export therefore
      // bypasses the reconciliation step entirely. The Lloyd's 5.2 columns show
      // locked/greyed; users add custom fields on top. The LimeIQ Advanced
      // Fields pop-up for non-superusers now fires on the Export/Save step
      // (alongside Recipients), not here — see the activeStep watcher.
      const targetTab = 4

      await this.$nextTick()
      if (document.activeElement) document.activeElement.blur()
      this.$refs.stepWizard.changeTab(0, targetTab)
      await this.$nextTick()
      this.isFetchingFullExport = false

      // The LimeIQ Advanced Fields pop-up (non-superuser) is no longer shown
      // here — it opens when the user reaches the Export/Save step, alongside
      // Recipients (see the activeStep watcher).
    },
    onSetTpaGroupName(enabled) {
      // Non-superuser opt-in for the TPA Group Name column (the Advanced Fields
      // toggle is shown to non-superusers only). Track the intent and reconcile
      // it into columns_to_show now for immediate UI consistency; submit-time
      // reconciliation is the authoritative pass.
      this.tpaGroupSelected = enabled
      this.reconcileTpaGroupColumn()
    },
    reconcileTpaGroupColumn() {
      // Keep the non-superuser Advanced Fields toggle (tpaGroupSelected) in sync with
      // the 'tpa_group_name' token. Superusers pick TPA Group Name via the Select
      // Columns checkbox instead, so they're not handled here.
      if (!this.isFullDataExport) return
      const include = this.tpaGroupSelected
      const columns = Array.isArray(this.savedReport.payload.columns_to_show)
        ? [...this.savedReport.payload.columns_to_show]
        : []
      const idx = columns.indexOf('tpa_group_name')
      if (include && idx === -1) columns.push('tpa_group_name')
      else if (!include && idx !== -1) columns.splice(idx, 1)
      this.savedReport.payload.columns_to_show = columns
    },
    async getSavedReportTemplate(reportId) {
      this.isFetchingReport = true

      const response = await ExportsService.getExport(reportId, false)

      this.isFetchingReport = false
      this.activeReport = response.data.data.data

      // Restore the saved schedule so amending repopulates the scheduler (esp.
      // the End Date, which otherwise resets to blank). These fields live on the
      // template record itself, not in the export payload JSON. Only build it
      // when the record actually has a schedule (an occurrence is set).
      const rec = response.data.data.data
      this.initialSchedule = rec && rec.occurrence
        ? {
            start_date: rec.start_date || null,
            end_date: rec.end_date || null,
            occurrence: rec.occurrence,
            day_of_week: rec.day_of_week || null,
            day_of_month: rec.day_of_month || null,
            time_of_day: rec.time_of_day || null,
            timezone: rec.timezone || null
          }
        : null

      const parsedPayload = JSON.parse(response.data.data.data.payload)
      parsedPayload.umr_criteria = parsedPayload.umr_criteria || 'Active'
      parsedPayload.export_type = parsedPayload.export_type || 'market_export'
      parsedPayload.group_by = parsedPayload.group_by || ['*']
      parsedPayload.jurisdiction = parsedPayload.jurisdiction || []
      parsedPayload.export_sub_type = parsedPayload.export_sub_type || 'lloyds'
      parsedPayload.all_exports = parsedPayload.all_exports || false

      if (4 === parsedPayload.group_by.length) parsedPayload.group_by = ['*']

      // Holds the original export payload
      this.savedReport.origPayload = parsedPayload // Non-reactive
      this.savedReport.payload = parsedPayload // Reactive

      this.claimingGroup = parsedPayload.group_by

      // If the saved template was a full data export, restore the full-export
      // flow on amend instead of falling into the normal "Select Filters" path
      // (onSelectReport resets isFullDataExport to false). The underwriter is
      // already known from the payload, so skip the underwriter-select screen.
      if (parsedPayload.all_exports) {
        this.isFullDataExport = true
        this.isSelectingUnderwriter = false
        // Restore the non-superuser toggle state from the saved template.
        this.tpaGroupSelected =
          Array.isArray(parsedPayload.columns_to_show) &&
          parsedPayload.columns_to_show.includes('tpa_group_name')
        if (this.currentUser.is_superuser) {
          // Reconstruct the selected underwriter from the saved payload so the
          // full-export controls (which read selectedUnderwriter.id) work.
          this.selectedUnderwriter = {
            id: parsedPayload.company_id,
            role_value: parsedPayload.role_value
          }
        }
      }

      this.isTemplateLoaded = true
    },
    async exportReport(showLoader = true, savedReportId = null) {
      if (showLoader) this.isExporting = true

      try {
        // Ensure the TPA Group Name column reflects the superuser/toggle rule
        // even if the column-selection machinery stripped the token mid-flow.
        this.reconcileTpaGroupColumn()
        // When triggered by "Save and Export", link the run to its schedule
        // so it rolls up under the schedule (Last Run / Run History) instead
        // of appearing as a separate one-time export.
        const basePayload = savedReportId
          ? { ...this.exportPayload, saved_report_id: savedReportId }
          : { ...this.exportPayload }

        // LIQ-2190 — carry the confirmed Cash Call Reconciliation with the
        // export so the backend applies the netting rules to the produced BDX
        // rows and column totals, writes any Paid/Outstanding changes back to
        // the Cash Call page, and logs overrides / "Other" entries against the
        // export session. A Full Data Export bypasses the step, so it sends
        // nothing and the export behaves exactly as before.
        const reconciliationRows =
          (this.cashCallReconciliation && this.cashCallReconciliation.rows) || []
        const payload = (!this.isFullDataExport && reconciliationRows.length)
          ? { ...basePayload, cash_call_reconciliation: reconciliationRows }
          : basePayload

        const exportResponse = await ReportsService.exportData(payload)

        if (showLoader) this.isExporting = false
        this.isTemplateLoaded = false

        if (this.isFullDataExport) {
          this.$toasted.show("Your Full Data Export is in progress. You will be emailed when it's ready.")
          const exportId = exportResponse && exportResponse.data && exportResponse.data.data && exportResponse.data.data.id
          this.$router.push({
            name: 'ExportLog',
            query: exportId ? { highlight: `report-${exportId}` } : {}
          })
        } else {
          this.$toasted.show(
              "Your export is in progress. You will be emailed when it's ready on the Export Log page."
          )
          this.$emit('report-exported', true)
        }
      } catch (e) {
        console.error('[FDE] exportReport failed:', e)
        if (this.isFullDataExport) {
          this.$toasted.error('Full Data Export failed. Please try again or contact support.')
        }
        if (showLoader) this.isExporting = false
      }
    },
    async saveReportTemplate() {
      // Start Date is required whenever a schedule is being configured — the
      // backend schedule builder needs it to create the recurring task. Block
      // (with a toast) rather than saving a schedule that would never fire.
      if (this.scheduleActive && !this.reportSchedule.start_date) {
        this.$toasted.error('Please select a Start Date for the scheduled export.')
        return
      }

      this.isSaving = true

      // Persist the correct TPA Group Name selection so scheduled/recurring
      // runs of this template keep (or omit) the column consistently.
      this.reconcileTpaGroupColumn()
      const response = await ExportsService.saveExport(
          this.activeReport.id,
          this.exportPayload,
          this.activeReport.name,
          this.activeReport.created_by,
          this.reportSchedule.occurrence,
          this.reportSchedule.time_of_day,
          this.reportSchedule.start_date,
          this.reportSchedule.end_date,
          this.reportSchedule.day_of_week,
          this.reportSchedule.day_of_month,
          this.reportSchedule.timezone
      )

      // The saved schedule's id, so the immediate export links back to it.
      // The API wraps responses as { status, data: { status, message, data } },
      // so the created record lives at response.data.data.data.
      const created =
          response &&
          response.data &&
          response.data.data &&
          response.data.data.data
      const savedReportId = (created && created.id) || this.activeReport.id || null

      // Save & Export fires an export now (linked to the saved schedule via
      // saved_report_id) AND saves the record — for both normal reports and
      // Full Data Exports. exportReport shows the correct in-progress toast
      // and redirect for each type (the FDE toast/redirect live there).
      await this.exportReport(false, savedReportId)
      this.isSaving = false

    },
    handleClearCriteriaFilters() {
      this.savedReport.payload.parameters = []
      this.savedReport.payload.flag = []
      this.savedReport.payload.umr_criteria = 'Active'
      this.savedReport.payload.jurisdiction = []
      this.$refs.criteriasRef.jurisdictionInitialSet = false

      this.$refs.criteriasRef.dateType = null
    },
    handleBeforeCriteriasChange() {
      const { parameters } = this.savedReport.payload

      if (!parameters.length) {
        return false
      } else {
        const paramNames = parameters.map((parameter) => parameter.name)
        this.savedReport.payload.all_exports = this.$refs.criteriasRef.dateType === 'all-exports';
        return (
            paramNames.includes('Reporting_Period_End_Date') ||
            paramNames.includes('modified_on') ||
            this.$refs.criteriasRef.dateType === 'all-exports'
        )
      }
    },
    /**
     * LIQ-2190 (story 4.5) — leaving the Cash Call Reconciliation step asks the
     * user to verify what they are about to export, because confirming both
     * applies the netting rules AND writes any Paid/Outstanding changes back to
     * the Cash Call page.
     *
     * A Full Data Export bypasses the step entirely, and a step with no matched
     * cash calls has nothing to verify, so both proceed straight through with
     * no prompt.
     */
    async handleBeforeCashCallReconciliationChange() {
      if (this.isFullDataExport) return true

      const rows = (this.cashCallReconciliation && this.cashCallReconciliation.rows) || []
      if (!rows.length) return true

      // "Other" is a manual entry, so the story requires a captured reason -
      // block progression until each one is explained (it is recorded against
      // the export session for the audit trail).
      const missingReason = rows.some((row) => row.action === 'Other' && !row.reason)
      if (missingReason) {
        this.$bvToast.toast(
          'Enter a reason for each row set to "Other" before continuing.',
          { title: 'Reason required', variant: 'warning', solid: true }
        )
        return false
      }

      // Spell out what is about to change on the Cash Call page. This is the
      // last point the TPA can go back, and marking a cash call paid is not
      // reversible from the export, so the counts are stated explicitly rather
      // than leaving the user to re-read the table.
      //
      // Only what THIS export changes is counted as "set to Paid". A cash call
      // already Paid on the Cash Call page is shown locked on the step and is
      // not touched by the export, so counting it as set to Paid overstated the
      // change - 6 already-paid rows and 1 toggled here read as "7 are set to
      // Paid". scanned_status is what the scan said before the user acted.
      const paidHere = rows.filter((row) => row.status === 'Paid' && row.scanned_status !== 'Paid').length
      const alreadyPaid = rows.filter((row) => row.scanned_status === 'Paid').length
      // "No, awaiting funds" partials: recorded on the Cash Call page, but the
      // cash call stays Outstanding.
      const partialAwaiting = rows.filter((row) => row.status !== 'Paid' && row.payment).length
      const outstandingCount = rows.filter((row) => row.status !== 'Paid').length
      const plural = (count) => (count === 1 ? 'cash call' : 'cash calls')
      const isAre = (count) => (count === 1 ? 'is' : 'are')
      const wasWere = (count) => (count === 1 ? 'was' : 'were')

      // "are set to Paid", not "will be set to Paid" (demo feedback).
      const parts = []
      if (paidHere) {
        parts.push(`${paidHere} ${plural(paidHere)} ${isAre(paidHere)} set to Paid on the Cash Call page.`)
      } else {
        parts.push('No cash call is set to Paid on the Cash Call page.')
      }
      if (partialAwaiting) {
        parts.push(`${partialAwaiting} partial ${partialAwaiting === 1 ? 'payment is' : 'payments are'} recorded, awaiting funds - ${partialAwaiting === 1 ? 'that cash call stays' : 'those cash calls stay'} Outstanding.`)
      }
      if (alreadyPaid) {
        parts.push(`${alreadyPaid} ${plural(alreadyPaid)} ${wasWere(alreadyPaid)} already Paid.`)
      }
      if (outstandingCount) {
        parts.push(`${outstandingCount} ${isAre(outstandingCount)} left Outstanding.`)
      }
      const summary = parts.join(' ')

      const message = this.$createElement('div', [
        this.$createElement(
          'p',
          { class: 'mb-2' },
          'Any status changes made here will update the Cash Call page and apply netting rules to your generated export.'
        ),
        this.$createElement('p', { class: 'mb-0 font-weight-bold' }, summary),
      ])

      const confirmed = await this.$bvModal.msgBoxConfirm(
        message,
        {
          title: 'Please verify export cash call information for accuracy.',
          okTitle: 'Confirm & Proceed',
          cancelTitle: 'Back to Review',
          okVariant: 'primary',
          cancelVariant: 'outline-secondary',
          centered: true,
          // The default header runs the title hard into both edges. This pulls
          // it in and centres it - styled globally (not scoped) because
          // msgBoxConfirm renders the modal at <body>, outside this component.
          headerClass: 'cash-call-verify__header',
          bodyClass: 'cash-call-verify__body',
        }
      )
      return !!confirmed
    },
    onSetSplitBy(payload) {
      if (payload) {
        const parts = payload.split('-')
        const pickAltColumnName = parts.length === 1
        const MARKET_DEFAULT = 'umr-risk_code-lloyds_cat_code-section_no-settlement_currency-year_of_account'

        if (this.savedReport.payload.export_type === 'market_export' && payload !== MARKET_DEFAULT) this.savedReport.payload.export_type = 'custom_export'

        if (this.savedReport.payload.export_type === 'custom_export' && payload === MARKET_DEFAULT) this.savedReport.payload.export_type = 'market_export';


        this.savedReport.payload.group_by = parts
            .map((item) => {
              const index = splitByOptions.findIndex(
                  (option) => option.value === item
              )

              if ('risk_code' === item)
                return {
                  value: 'risk_code',
                  columnName: 'Risk_Code'
                }

              return splitByOptions[index]
            })
            .filter((item) => item)
            .map((item) =>
                pickAltColumnName && item.altColumnName
                    ? item.altColumnName
                    : item.columnName
            )
      } else {
        if (undefined === payload || '' === payload) {
          this.savedReport.payload.group_by = ['Unique_Market_Reference_UMR']
        } else {
          this.savedReport.payload.group_by = splitByOptions
              .filter((option) => option.isDefault)
              .map((option) => option.columnName)
        }
      }
    }
  },
  watch: {
    'savedReport.payload.parameters'(newValue) {
      // Override original payload on "Select Criteria" tab
      if (2 == this.activeStep)
        this.savedReport.origPayload.parameters = newValue
    },
    activeStep(newStep) {
      // Split Categories — activeStep 4 since LIQ-2190 inserted the Cash Call
      // Reconciliation step ahead of it.
      if (newStep === 4) {
        this.savedReport.payload.export_type =
            this.savedReport.payload.export_type || 'market_export';
      }
      // Non-superuser Full Data Export: show the LimeIQ Advanced Fields pop-up
      // when they reach the Export/Save step (activeStep 6), i.e. right after
      // the Select Columns page, alongside Recipients.
      if (
        newStep === 6 &&
        this.isFullDataExport &&
        !this.currentUser.is_superuser
      ) {
        this.$nextTick(() => this.$bvModal.show('custom-fields-modal'))
      }
    },
    // Amend can be triggered while this wizard is already mounted (e.g. from the
    // Saved Reports & Scheduling table on this same page). mounted() only reads
    // ?amend once, so watch the query too and load the template on change.
    '$route.query.amend'(amendId, prev) {
      if (amendId && amendId !== prev) {
        this.$nextTick(() => this.onSelectReport(amendId))
      }
    },
    'savedReport.payload.export_type'(newType, oldType) {
      if (newType !== oldType) {
        // Only reset columns if the template hasn't been loaded
        if (!this.isTemplateLoaded) this.savedReport.payload.columns_to_show = []

        if (newType === 'market_export') {
           this.savedReport.payload.export_by = 'umr-risk_code-lloyds_cat_code-section_no-settlement_currency-year_of_account'
        }

        // Wait for DOM/reactivity to catch up, then re-trigger child setup
        this.$nextTick(() => {
          const columnsComponent = this.$refs.columnsRef; // We'll add ref below

          if (columnsComponent && typeof columnsComponent.selectFilter === 'function') {
            columnsComponent.selectFilter(this.savedReport.payload.export_sub_type);
          }
        });
      }
    },
  },
  mounted() {
    if (!this.isUserRole('Superuser'))
      this.savedReport.payload.limeiq_user_email = this.currentUser.email

    this.$bus.$on('error-catched', () => {
      this.isExporting = false
      this.isSaving = false
    })

    // Amend flow: when the Export Log page links here with ?amend=<id>,
    // open that saved schedule pre-filled for editing.
    const amendId = this.$route.query.amend
    if (amendId) {
      this.$nextTick(() => this.onSelectReport(amendId))
    }
  }
}
</script>

<style lang="scss">

// Cash Call Reconciliation verify modal. Deliberately outside #save-report-wizard:
// msgBoxConfirm renders the modal at <body>, so a nested selector never reaches it.
.cash-call-verify__header {
  // The default header runs the title hard into both edges and leaves the
  // close button crowding it. Centre the title and give it real room.
  padding: 22px 32px 18px;
  align-items: flex-start;

  .modal-title {
    width: 100%;
    text-align: center;
    font-size: 1.05rem;
    line-height: 1.45;
    // Clear of the absolutely-positioned close button at either side.
    padding: 0 26px;
  }

  .close {
    position: absolute;
    right: 20px;
    top: 18px;
    margin: 0;
    padding: 0;
  }
}

.cash-call-verify__body {
  padding: 4px 32px 22px;
  text-align: center;
}

#save-report-wizard {
  .wizard {
    &-progress-with-circle::after {
      width: 9.6%;
    }

    &-tab-content,
    &-card-footer {
      max-width: 81%;
    }

    &-tab-content {
      margin-bottom: 35px;
    }
  }
}

.full-export-mode {
  .wizard-nav-pills {
    li {
      pointer-events: none;
    }

    li:nth-child(2),
    li:nth-child(3),
    li:nth-child(4) {

      .wizard-icon-circle {
        background-color: #e9ecef !important;
        border-color: #dee2e6 !important;

        .wizard-icon,
        .wizard-icon-container {
          color: #adb5bd !important;
        }
      }

      .stepTitle {
        color: #adb5bd !important;
      }
    }
  }
}

.full-export-overlay {
  position: absolute;
  inset: 0;
  z-index: 2000;
  background: rgba(255, 255, 255, 0.92);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &__spinner {
    font-size: 2.5rem;
    color: #1c4a50;
    margin-bottom: 0.75rem;
  }

  &__title {
    font-weight: 600;
    color: #1c4a50;
    font-size: 1.05rem;
    margin: 0 0 0.25rem;
  }

  &__sub {
    color: #6c757d;
    font-size: 0.875rem;
    margin: 0;
  }
}

.full-export-fade-enter-active,
.full-export-fade-leave-active {
  transition: opacity 0.25s ease;
}

.full-export-fade-enter,
.full-export-fade-leave-to {
  opacity: 0;
}

.vue-option-list__item-label::before {
  left: 1px !important;
}

.vue-option-list__item-label::after {
  left: 9px !important;
}

.export-summary-card {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  max-width: 560px;

  &__list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__row {
    display: flex;
    gap: 1rem;

    dt {
      flex: 0 0 140px;
      font-weight: 600;
      color: #495057;
      font-size: 0.875rem;
    }

    dd {
      flex: 1;
      margin: 0;
      color: #212529;
      font-size: 0.875rem;
      word-break: break-word;
    }
  }
}
</style>
